import React, { useEffect, useState } from 'react';
import { Observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Formik } from 'formik';
import {
  MenuItem,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Typography,
  Checkbox,
  FormControlLabel,
  Paper,
  ClickAwayListener,
  Menu,
} from '@material-ui/core';
import { css } from '@emotion/core';
import CloseIcon from '@material-ui/icons/Close';
import { FixedSizeList as List } from 'react-window';
import { toJS } from 'mobx';
import CheckIcon from '@material-ui/icons/Check';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { isWithinRange, isBefore, isAfter } from 'date-fns';
import { safeEval } from '../../../util/UtilFunctions';
import SimpleSelect from '../../../util/SimpleSelect';
import { useRootStore } from '../../../../stores/BridgeProvider';
import { DealStatusEnums } from '../../../common/Enums';
import RenderPropModal from '../../common/RenderPropModal';
import { FlexDiv, FormRow } from '../../../styles/styles';
import DealModal from './DealModal';

const SearchHeader = styled.div`
  padding: 0px 0px;
  outline: 0 !important;
  display: block;
`;

const EmptyResultList = styled.div`
  color: #666;
  text-align: center;
  padding: 1rem 0px;
`;

const HeaderItems = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 16px 2px 12px 8px;
  align-items: center;
  box-sizing: border-box;
`;

const DealRow = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: center;
  color: #666;
  .dRLeftSide {
    display: flex;
    flex-direction: column;
  }
`;

const initialValues = {
  dealSearch: '',
  unmappedOnly: false,
  status: 'all',
  titleSearch: '',
  dealNumberSearch: '',
  descriptionSearch: '',
  contactNameSearch: '',
  startDate: '',
  endDate: '',
};

const dealFilter = ({ deals, dmDeals, values }) => {
  const {
    dealSearch,
    unmappedOnly,
    status,
    titleSearch,
    dealNumberSearch,
    descriptionSearch,
    contactNameSearch,
    startDate,
    endDate,
  } = values;
  const adjustedSearch = dealSearch.toLowerCase();

  const inProperties = (vals, searchString) => {
    if (!searchString || searchString.length === 0) return true;
    return vals.some(value =>
      safeEval(() => value.toLowerCase().includes(searchString), false),
    );
  };

  const isValidMapping = id =>
    safeEval(() => {
      if (!unmappedOnly) return true;
      return dmDeals.findIndex(({ DealId }) => DealId === id) === -1;
    }, false);

  const isValidStatus = id =>
    safeEval(() => {
      if (status === 'all') return true;
      return id === status;
    }, false);

  const isValidTransactionDate = dDate => {
    if (startDate.length > 0 && endDate.length > 0) {
      if (isBefore(endDate, startDate)) return false;
      return isWithinRange(dDate, startDate, endDate);
    }
    if (startDate.length > 0) {
      return isAfter(dDate, startDate);
    }
    if (endDate.length > 0) {
      return isBefore(dDate, endDate);
    }
    return true;
  };

  return deals.filter(
    ({
      Title,
      DealId,
      StatusId,
      Description,
      DealNumber,
      AgentContactName,
      TransactionDate,
    }) => {
      const conditions = [
        isValidStatus(StatusId),
        isValidMapping(DealId),
        isValidTransactionDate(TransactionDate),
        inProperties([Title], titleSearch.toLowerCase()),
        inProperties([DealNumber], dealNumberSearch.toLowerCase()),
        inProperties([AgentContactName], contactNameSearch.toLowerCase()),
        inProperties([Description], descriptionSearch.toLowerCase()),
        inProperties(
          [Title, DealNumber, Description, AgentContactName],
          adjustedSearch,
        ),
      ];
      return conditions.every(Boolean);
    },
  );
};

const FilterOptions = ({
  handleBlur,
  handleChange,
  values,
  resetForm,
  dirty,
  setFieldValue,
  closeFilters,
}) => {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        padding: 10px 5px;
        outline: 0 !important;
      `}
    >
      <Typography variant="h5" style={{ textAlign: 'center' }}>
        Deal Filters
      </Typography>

      <FormRow>
        <TextField
          name="titleSearch"
          label="Deal Title"
          value={values.titleSearch}
          margin="normal"
          onChange={handleChange}
          onBlur={handleBlur}
          variant="outlined"
          fullWidth
        />
      </FormRow>
      <FormRow>
        <SimpleSelect
          label="Deal Status"
          handleChange={handleChange}
          value={values.status}
          name="status"
          additionalMenuProps={{ disablePortal: true }}
        >
          <MenuItem value="all">All</MenuItem>
          {DealStatusEnums.map((value, i) => (
            <MenuItem key={value} value={i}>
              {value}
            </MenuItem>
          ))}
        </SimpleSelect>
        <TextField
          name="contactNameSearch"
          label="Agent Name"
          value={values.contactNameSearch}
          margin="normal"
          onChange={handleChange}
          onBlur={handleBlur}
          variant="outlined"
          fullWidth
        />
      </FormRow>
      <FormRow>
        <TextField
          name="dealNumberSearch"
          label="Deal Number"
          value={values.dealNumberSearch}
          margin="normal"
          onChange={handleChange}
          onBlur={handleBlur}
          variant="outlined"
          fullWidth
        />
        <TextField
          name="descriptionSearch"
          label="Description"
          value={values.descriptionSearch}
          margin="normal"
          onChange={handleChange}
          onBlur={handleBlur}
          variant="outlined"
          fullWidth
        />
      </FormRow>
      <FormRow>
        <TextField
          name="startDate"
          label="After Date"
          margin="normal"
          value={values.startDate}
          onChange={handleChange}
          onBlur={handleBlur}
          variant="outlined"
          type="date"
          fullWidth
          InputLabelProps={{
            shrink: true,
          }}
        />
        <TextField
          name="endDate"
          label="Before Date"
          margin="normal"
          value={values.endDate}
          onChange={handleChange}
          onBlur={handleBlur}
          variant="outlined"
          type="date"
          fullWidth
          InputLabelProps={{
            shrink: true,
          }}
        />
      </FormRow>

      <FormControlLabel
        control={
          <Checkbox
            checked={!!values.unmappedOnly}
            onChange={(e, val) => {
              setFieldValue('unmappedOnly', val);
            }}
            value={values.unmappedOnly}
          />
        }
        label="Unmapped only"
      />

      <Button
        size="small"
        color="primary"
        variant="contained"
        onClick={() => resetForm(initialValues)}
        disabled={!dirty}
        style={{ marginBottom: '5px' }}
      >
        Reset
      </Button>
      <Button
        color="primary"
        size="small"
        variant="contained"
        onClick={closeFilters}
      >
        Done
      </Button>
    </div>
  );
};

const DealSearch = () => (
  <Formik
    initialValues={initialValues}
    render={formikProps => (
      <Observer
        render={() => {
          const { dataStore, dealMapperStore } = useRootStore();
          const [open, setOpen] = useState(false);
          const [showFilters, setShowFilters] = useState(false);
          const { loadDeals } = dealMapperStore;
          const deals = dataStore.dealsArray;
          const [anchorEl, setAnchorEl] = useState(null);

          const handleMenuClick = event => {
            setAnchorEl(event.currentTarget);
          };

          const handleMenuClose = () => {
            setAnchorEl(null);
          };

          useEffect(() => {
            const initialize = async () => {
              if (deals.length === 0) {
                await dataStore.retrieveDeals();
              }
            };
            initialize();
          }, []);
          const {
            handleChange,
            values,
            resetForm,
            dirty,
            setFieldValue,
          } = formikProps;
          const dealList = dealFilter({
            deals,
            dmDeals: dealMapperStore.deals,
            values,
          });

          const Row = ({ index, style }) => (
            <Observer
              render={() => {
                const deal = dealList[index - 1] || null;
                if (index === 0) {
                  return (
                    <MenuItem
                      disabled={dealList.length > 20}
                      style={style}
                      onClick={() =>
                        loadDeals(dealList.map(({ DealId }) => DealId))
                      }
                    >
                      <div
                        css={css`
                          display: flex;
                          flex-direction: column;
                          color: #666;
                        `}
                      >
                        Add all listed Deals ({dealList.length})
                      </div>
                    </MenuItem>
                  );
                }
                const isUnMapped = id =>
                  safeEval(() => {
                    return (
                      dealMapperStore.deals.findIndex(
                        ({ DealId }) => DealId === id,
                      ) === -1
                    );
                  }, false);
                return (
                  <MenuItem
                    key={deal.DealId}
                    value={deal.DealId}
                    style={style}
                    onClick={() => loadDeals([deal.DealId])}
                  >
                    <DealRow>
                      <div className="dRLeftSide">
                        <span>
                          <b>{deal.Title}</b>
                        </span>
                        <span>{deal.StatusName}</span>
                      </div>
                      {!isUnMapped(deal.DealId) && <CheckIcon />}
                    </DealRow>
                  </MenuItem>
                );
              }}
            />
          );

          return (
            <RenderPropModal
              cardDisplay={({ handleClose }) => (
                <FlexDiv>
                  <DealModal
                    handleClose={() => {
                      handleClose();
                      setOpen(false);
                    }}
                  />
                </FlexDiv>
              )}
            >
              {({ handleOpen }) => (
                <FormRow style={{ width: '360px' }}>
                  <ClickAwayListener
                    onClickAway={() => {
                      if (open) {
                        setOpen(false);
                      }
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        outline: '0',
                        position: 'relative',
                      }}
                    >
                      <TextField
                        name="dealSearch"
                        label="Add Deals"
                        value={values.dealSearch}
                        margin="normal"
                        onChange={handleChange}
                        onClick={() => setOpen(true)}
                        variant="outlined"
                        fullWidth
                        InputProps={
                          values.dealSearch.length > 0
                            ? {
                                autoComplete: 'off',
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      size="small"
                                      aria-label="clearSearch"
                                      onClick={() =>
                                        setFieldValue('dealSearch', '')
                                      }
                                    >
                                      <CloseIcon />
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }
                            : { autoComplete: 'off' }
                        }
                      />
                      <Paper
                        style={{
                          display: open ? 'inherit' : 'none',
                          position: 'absolute',
                          width: '360px',
                          zIndex: 1000,
                        }}
                      >
                        <SearchHeader
                          onClick={e => {
                            e.stopPropagation();
                          }}
                        >
                          {!showFilters && (
                            <HeaderItems>
                              <Button
                                onClick={() => setShowFilters(true)}
                                color={dirty ? 'primary' : 'default'}
                                variant="outlined"
                              >
                                Advanced Filters {dirty && '(Active)'}
                              </Button>
                              <IconButton
                                onClick={handleMenuClick}
                                style={{ marginTop: '-3px' }}
                              >
                                <MoreVertIcon />
                              </IconButton>
                              <Menu
                                anchorEl={anchorEl}
                                getContentAnchorEl={null}
                                anchorOrigin={{
                                  vertical: 'bottom',
                                  horizontal: 'left',
                                }}
                                keepMounted
                                open={Boolean(anchorEl)}
                                onClose={handleMenuClose}
                                disablePortal
                              >
                                <span />
                                <MenuItem
                                  onClick={() => {
                                    handleMenuClose();
                                    handleOpen();
                                  }}
                                >
                                  New Deal
                                </MenuItem>
                              </Menu>
                            </HeaderItems>
                          )}

                          {showFilters && (
                            <FilterOptions
                              {...formikProps}
                              closeFilters={() => setShowFilters(false)}
                            />
                          )}
                        </SearchHeader>

                        <div value="" />
                        {!showFilters && dealList.length === 0 && (
                          <EmptyResultList>No results found</EmptyResultList>
                        )}
                        {!showFilters && dealList.length > 0 && (
                          <List
                            height={Math.min(450, (dealList.length + 1) * 65)}
                            itemCount={dealList.length + 1}
                            itemSize={65}
                          >
                            {Row}
                          </List>
                        )}
                      </Paper>
                    </div>
                  </ClickAwayListener>
                </FormRow>
              )}
            </RenderPropModal>
          );
        }}
      />
    )}
  />
);

export default DealSearch;
